import { PropsWithChildren, useContext } from 'react'
import { LootContext } from '../context'
import { usePageInfo } from 'app/frontend/base/config/queries'
import Select from 'app/frontend/base/components/Select'
import Season from 'app/frontend/types/custom/Season'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Percent } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type Props = {
  disabled?: {
    season?: boolean
    valueDisplay?: boolean
  }
  onSeasonChange?: (season: Season) => void
}

const OptionBar = ({
  children,
  disabled,
  onSeasonChange,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation()
  const {
    data: {
      currentExpansion: { seasons },
    },
  } = usePageInfo()

  const { selectedSeason, setSelectedSeason, valueDisplay, setValueDisplay } =
    useContext(LootContext)

  return (
    <div className='flex items-center gap-2 flex-wrap'>
      {children}

      {seasons.length > 1 && (
        <Select
          className='w-52'
          value={disabled?.season ? undefined : selectedSeason.id.toString()}
          placeholder='Select season'
          options={seasons.map((season) => ({
            value: season.id.toString(),
            label:
              season.instances.length > 1
                ? `${season.name} (${season.expansion})`
                : season.instances[0].name,
            description:
              season.instances.length > 1
                ? season.instances.map((instance) => instance.name).join(', ')
                : `${season.name} (${season.expansion})`,
            sortLabel: season.name,
          }))}
          setValue={(value) => {
            const newSeason = seasons.find(
              (season) => season.id.toString() === value,
            ) as Season

            setSelectedSeason(newSeason)
            onSeasonChange?.(newSeason)
          }}
        />
      )}

      <Tabs
        value={valueDisplay}
        onValueChange={(value) => {
          setValueDisplay(value)
        }}
      >
        <TabsList className='h-10'>
          <TabsTrigger value='percentage' disabled={disabled?.valueDisplay}>
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <Percent size={16} />
              </TooltipTrigger>
              <TooltipContent>
                {t('dashboard.team.loot.percentage-view')}
              </TooltipContent>
            </Tooltip>
          </TabsTrigger>
          <TabsTrigger value='absolute' disabled={disabled?.valueDisplay}>
            <Tooltip delayDuration={500}>
              <TooltipTrigger className='font-mono'>123</TooltipTrigger>
              <TooltipContent>
                {t('dashboard.team.loot.absolute-view')}
              </TooltipContent>
            </Tooltip>
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  )
}

export default OptionBar
